import { useQueryClient } from '@tanstack/react-query'
import useAppMutation from 'hooks/use-app-mutation'

import {
  CRMPlaceAutocompletePayload,
  crmGetPlaceAutocomplete,
  crmGetPlaceDetails,
  Prediction,
  PlaceDetailsResponse,
} from 'services/place-search'
import { Success } from 'store/types'

// query cache key prefix
const PLACE_PREDICTION_KEY = 'placePredictions'
const PLACE_DETAILS_KEY = 'placeDetails'

export const usePlaceAutocomplete = () => {
  const queryClient = useQueryClient()

  const { mutateAsync, isLoading, isError } = useAppMutation(
    [],
    async ({ input, sessiontoken, options }: CRMPlaceAutocompletePayload) => {
      // Check cache first
      const cachedData = queryClient.getQueryData<Prediction[]>([PLACE_PREDICTION_KEY, input])

      if (cachedData) {
        return Promise.resolve({ data: cachedData, status: 'SUCCESS' })
      }

      return await crmGetPlaceAutocomplete({ input, sessiontoken, options })
    },
    {
      onSuccess(data, variables) {
        queryClient.setQueryData([PLACE_PREDICTION_KEY, variables.input], data)
      },
      onError: (error) => {
        console.error('Search failed:', error)
      },
    },
  )

  return {
    getPlaceAutoComplete: mutateAsync,
    isLoading,
    isError,
  }
}

interface PlaceDetailsPayload {
  placeId: string
}

export const usePlaceDetails = () => {
  const queryClient = useQueryClient()

  const { mutateAsync, isLoading, isError } = useAppMutation(
    [],
    async ({ placeId }: PlaceDetailsPayload) => {
      // Check cache first
      const cachedData = queryClient.getQueryData<PlaceDetailsResponse>([PLACE_DETAILS_KEY, placeId])

      if (cachedData) {
        return Promise.resolve({ data: cachedData, status: 'SUCCESS' }) as Promise<Success<PlaceDetailsResponse>>
      }

      return await crmGetPlaceDetails({ place_id: placeId })
    },
    {
      onSuccess(data, variables) {
        queryClient.setQueryData([PLACE_DETAILS_KEY, variables.placeId], data)
      },
      onError: (error) => {
        console.error('Search failed:', error)
      },
    },
  )

  return {
    getPlaceDetails: mutateAsync,
    isPlaceDetailsLoading: isLoading,
    isPlaceDetailsError: isError,
  }
}
